import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import queryString from 'query-string'
import { ApiMiddlewarePayload, ApiResponse } from '@ocean/api'
import { SessionUtils, LoggerUtils } from '@ocean/utils'
import { LoginType } from 'types'
import { AppThunk } from 'store'
import { createApiAction } from 'api'
import { UserUtils, SsoUtils, EnvironmentUtils } from 'utils'
import { userLoggedOut, logoutUser } from 'core/session/slices/session'
import { redirectToSta, redirectToStaWithBackUrl } from 'core/staIntegration/slices/staIntegration'
import { getUserDeclaration } from 'core/userDeclaration/slices/userDeclaration'
import { getAccount } from 'modules/Auth/api/authService'
import authRoutes from 'modules/Auth/routing/routes'
import { setExternalLoginError, getAccountRequested } from 'modules/Auth/slices/auth'
import { GetAccountResponse, AccountTypes, LoginQueryParams } from 'modules/Auth/types'
import homeRoutes from 'modules/Home/routing/routes'
import { loginWithSso } from 'modules/Sso/api/ssoService'
import ssoRoutes from 'modules/Sso/routing/routes'
import { LoginWithSsoModel } from 'modules/Sso/types'
import simulatorTrainingInstructorILSimulatorListRoutes from 'modules/simulatorTrainings/instructor/instructorLed/SimulatorList/routing/routes'
import { SsoAuthenticationState, ApiSsoAuthenticationResponse } from './types'

const initialState: SsoAuthenticationState = {
  authenticationWithSso: {},
}

const ssoRedirectSlice = createSlice({
  name: 'sso',
  initialState,
  reducers: {
    userSsoAuthenticated(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiSsoAuthenticationResponse>>
    ) {
      state.authenticationWithSso = action.payload
    },
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const { userSsoAuthenticated } = ssoRedirectSlice.actions

export default ssoRedirectSlice.reducer

const handleSsoAuthentication =
  (response: ApiMiddlewarePayload<ApiSsoAuthenticationResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response

    dispatch(setExternalLoginError(undefined))

    if (error) {
      const { customer, userId } = error.details || {}
      UserUtils.storeUserLoginParams({ customer, userId, password: '', loginType: LoginType.Sso })
      dispatch(setExternalLoginError(error))
      const ecnCustomer = SsoUtils.getSsoMarker()
      if (ecnCustomer) {
        dispatch(push(`${ssoRoutes.logout}?ecn=${ecnCustomer}`))
      } else {
        dispatch(push(authRoutes.login))
      }
      return
    }

    if (!error && result?.ssoAuthentication) {
      const { customer, userId, sessionToken, isOceanEnabled, shouldUseLegacyClient } =
        result.ssoAuthentication
      SessionUtils.storeSession(sessionToken)
      UserUtils.storeUserLoginParams({ customer, userId, password: '', loginType: LoginType.Sso })

      const account: ApiResponse<GetAccountResponse> = await getAccount()
      const { success, content } = account

      if (success && content) {
        let redirectRoute: string | undefined

        // Checking for legacy params from STA - backUrl means redirect to STA
        const queryParams: LoginQueryParams = queryString.parse(window.location.search)
        const { backUrl, returnUrl } = queryParams

        if (backUrl) {
          dispatch(redirectToStaWithBackUrl(backUrl))
          return
        }

        UserUtils.storeUserAccountInfo(content)
        dispatch(getAccountRequested(content))

        // Checking special user and redirect to STA
        if (content.type === AccountTypes.SpecialUser) {
          LoggerUtils.info('Logged in as SpecialUser')
          const { simulatorDashboardForAdministratorsAvailable } =
            EnvironmentUtils.getPermissions('simulatorTraining')
          if (simulatorDashboardForAdministratorsAvailable) {
            redirectRoute = simulatorTrainingInstructorILSimulatorListRoutes.root
          } else {
            dispatch(redirectToSta())
            return
          }
        }

        // Checking user declaration for trainees
        if (!content.isDeclarationConfirmed) {
          LoggerUtils.info("User declaration didn't confirmed")
          dispatch(getUserDeclaration())
          return
        }

        // Checking is Ocean Platform Enabled
        if (!isOceanEnabled) {
          LoggerUtils.info('OCEAN Platform disabled')
          dispatch(redirectToSta())
          return
        }

        if (shouldUseLegacyClient) {
          dispatch(redirectToSta({ pluginName: 'TaskStatusPlugin', moduleName: null }))
          return
        }

        // Checking previous opened URL
        if (returnUrl) {
          dispatch(push(returnUrl))
          return
        }
        dispatch(push(redirectRoute ?? homeRoutes.root))
      } else {
        dispatch(logoutUser())
        UserUtils.storeUserLoginParams({ customer, userId, password: '', loginType: LoginType.Sso })
        dispatch(setExternalLoginError(account.error))
      }
    }

    dispatch(userSsoAuthenticated(response))
  }

export const authenticateWithSso = (ssoLoginModel: LoginWithSsoModel) => {
  return createApiAction({
    apiCalls: {
      ssoAuthentication: () => loginWithSso(ssoLoginModel),
    },
    payloadAction: userSsoAuthenticated,
    nextAction: handleSsoAuthentication,
  })
}
